// Install-prompt plumbing for the PWA.
//
// Chrome/Edge fire `beforeinstallprompt` very early - often before React has
// mounted - so this module is imported from index.js and starts listening at
// load time, holding the event until the install modal is ready to use it.

export const SNOOZE_KEY = 'famconnect:pwa-install-snoozed-until';
const SNOOZE_DAYS = 7;

let deferredPrompt = null;
let installed = false;
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error('PWA install listener failed:', error);
    }
  });
}

export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    // iOS Safari's own flag, set when the app was added to the home screen
    window.navigator.standalone === true
  );
}

export function isIOS() {
  const ua = window.navigator.userAgent || '';
  // iPadOS 13+ reports itself as a Mac, so the touch check catches those too.
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document)
  );
}

export function isMobile() {
  const ua = window.navigator.userAgent || '';
  return isIOS() || /Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

export function isInstalled() {
  return installed || isStandalone();
}

export function getDeferredPrompt() {
  return deferredPrompt;
}

export function isSnoozed() {
  try {
    const until = Number(window.localStorage.getItem(SNOOZE_KEY));
    return Boolean(until) && Date.now() < until;
  } catch (error) {
    return false;
  }
}

export function snooze() {
  try {
    window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000));
  } catch (error) {
    // Private mode / storage disabled - the prompt simply returns next visit.
  }
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Returns 'accepted', 'dismissed', or 'unavailable' when the browser never gave
// us a prompt to show (iOS, or an already-consumed event).
export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';

  const event = deferredPrompt;
  // The event can only be used once.
  deferredPrompt = null;
  notify();

  try {
    event.prompt();
    const { outcome } = await event.userChoice;
    return outcome === 'accepted' ? 'accepted' : 'dismissed';
  } catch (error) {
    console.error('Install prompt failed:', error);
    return 'dismissed';
  }
}

export function init() {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Stop the browser's own mini-infobar so ours is the only prompt shown.
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    deferredPrompt = null;
    notify();
  });
}
